import { Box, Drawer, IconButton, Stack } from "@mui/material";
import { NavMenu } from "./NavMenu";
import { UserProfile } from "./UserProfile";
import { SSButton } from "src/components/base/components/SSButton";
import { routes } from "src/shared/routes";
import React from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTeamConfig } from "./TeamConfigProvider";
import { useRouterExt } from "../hooks/useRouterExt";
import { useLogout } from "../hooks/useLogout";
import { useGlobalStore } from "../hooks/useGlobalStore";
import { SlMenu } from "react-icons/sl";
import { SSChip } from "src/components/base/components/SSChip";
import { FiUser } from "react-icons/fi";
import { useSSTheme } from "../hooks/useSSTheme";
import { TOAuthState } from "src/shared/types/globalStotre.types";
import { constants } from "src/shared/constants";
import { useGroupInviteFlow } from "src/components/feature/groupInviteFlow/useGroupInviteFlow";
import { useTicketsdotcom } from "../hooks/useTicketsdotcom";
import { useSeatGeek } from "../hooks/useSeatGeek";
import { useTicketmaster } from "../hooks/useTicketmaster";

export function NavBarHamburgerMenu() {
  const theme = useSSTheme();
  const teamConfig = useTeamConfig();
  const { TEAM_GLOBAL_SETTINGS, VENDOR } = teamConfig;
  const router = useRouterExt();
  const urlCode = router?.query?.urlCode;
  const { session } = useGlobalStore();
  const { handleLogout } = useLogout();
  const { isGroupInviteFlow } = useGroupInviteFlow();
  const { connectSeatGeek } = useSeatGeek();
  const { connectTicketmaster } = useTicketmaster();
  const { connectTicketsdotcom } = useTicketsdotcom();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isSigningOut, setIsSigningOut] = React.useState(false);
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const onClickLogin = async () => {
    try {
      setIsLoggingIn(true);
      let state: TOAuthState = {};
      if (isGroupInviteFlow) {
        state = {
          authOrigin: constants.authOrigins.groupInviteLogin,
          redirectRoute: {
            pathname: routes.group_invite,
            query: router.query,
          },
        };
      } else {
        state = {
          authOrigin: constants.authOrigins.general,
          redirectRoute: {
            pathname:
              TEAM_GLOBAL_SETTINGS?.DEFAULT_APP_ROUTE_AFTER_LOGIN ||
              routes.home,
            query: {
              ...(urlCode && { urlCode }),
            },
          },
        };
      }

      if (VENDOR === constants.vendorUUIDs.seatgeek) {
        return await connectSeatGeek(state);
      }

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        return await connectTicketsdotcom(state);
      }

      await connectTicketmaster({ state });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Box>
        {session ? (
          <IconButton size="large" onClick={() => toggleSidebar()}>
            <SlMenu fontSize={"24px"} />
          </IconButton>
        ) : null}

        {!session && !TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_AUTH_BUTTONS ? (
          <SSChip
            isLoading={isLoggingIn}
            icon={<FiUser fontSize={"24px"} color={theme.colors.gray["300"]} />}
            size="medium"
            label={TEAM_GLOBAL_SETTINGS?.NAV_MENU?.LOGIN_LABEL || "Log In"}
            variant="outlined"
            clickable
            sx={{
              color: theme.colors.gray["300"],
              borderRadius: "4px",
              fontWeight: 500,
              fontSize: theme.pxToRem(theme.text.baseFontSize),
              border: "none",
              "& .MuiCircularProgress-root ": {
                color: `${theme.colors.gray["400"]} !important`,
                width: "20px !important",
                height: "20px !important",
              },
            }}
            onClick={onClickLogin}
          />
        ) : null}
      </Box>
      <Drawer
        anchor="right"
        open={isSidebarOpen}
        onClose={() => toggleSidebar()}
      >
        <Stack
          p={3}
          minWidth={"330px"}
          justifyContent={"space-between"}
          height={"100%"}
          position={"relative"}
        >
          <Box position={"absolute"} top={"12px"} right={"12px"}>
            <IconButton onClick={() => toggleSidebar()}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Stack>
            <Box mt={2}>
              <UserProfile />
            </Box>

            <Box mt={4}>
              <NavMenu />
            </Box>
          </Stack>

          {!TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_AUTH_BUTTONS &&
          !TEAM_GLOBAL_SETTINGS?.NAV_MENU?.HIDE_SIGN_OUT_BUTTON ? (
            <Box mt={2}>
              <SSButton
                loading={isSigningOut}
                size="large"
                onClick={async () => {
                  setIsSigningOut(true);
                  toggleSidebar();
                  await handleLogout();
                  setIsSigningOut(false);
                }}
                variant="contained"
                colorScheme="#000000"
                fullWidth
              >
                Sign Out
              </SSButton>
            </Box>
          ) : null}
        </Stack>
      </Drawer>
    </>
  );
}
